// Fase 1 do "garantir que o cartão foi concluído E estornado" (2026-06-29): selo de estado REAL do MP,
// comprovante de estorno (prova pro cliente levar ao banco) e auditor de cobertura (pagamentos no MP
// sem registro local). Consome /api/cartao/cobrancas/:id/estorno e /api/cartao/auditor. NÃO executa
// estorno (auto-refund = fase 2). Usado pela aba Conciliação Bancária.
import { useEffect, useState } from 'react';
import { FileText, Copy, Printer, AlertTriangle, Search, Loader2, CheckCircle2, RefreshCw } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../Toast';
import { useDialog } from '../DialogProvider';
import { Modal } from '../Modal';
import { formatMoeda } from '@fiodecobre/shared';

// ---- Estado verdadeiro do cartão (espelha classificarEstadoMp do backend) ----
export type EstadoCartaoMp =
  | 'concluida' | 'estornada' | 'estorno_parcial' | 'estorno_nao_confirmado' | 'estornada_sem_baixa';

export function classificarCartaoMp(args: {
  statusLocal: string; valor: number; estornoValor: number;
}): EstadoCartaoMp {
  const { statusLocal, valor, estornoValor } = args;
  const localDizEstornado = statusLocal === 'estornado' || statusLocal === 'desconciliado';
  const temEstorno = estornoValor > 0.005;
  const total = estornoValor >= valor - 0.01;
  if (localDizEstornado) {
    if (!temEstorno) return 'estorno_nao_confirmado';
    return total ? 'estornada' : 'estorno_parcial';
  }
  if (temEstorno) return total ? 'estornada_sem_baixa' : 'estorno_parcial';
  return 'concluida';
}

// Selo daltonismo-safe (texto + amarelo/vermelho, sem verde/vermelho ambíguo). Só aparece quando há
// algo a dizer sobre o estorno; 'concluida' não rende selo (não polui a linha normal).
export function SeloEstorno({ estado }: { estado: EstadoCartaoMp }) {
  const mapa: Record<EstadoCartaoMp, { txt: string; bg: string; fg: string } | null> = {
    concluida: null,
    estornada: { txt: 'Estornado ✓', bg: '#fde68a', fg: '#000' },
    estorno_parcial: { txt: 'Estorno parcial', bg: '#f97316', fg: '#000' },
    estorno_nao_confirmado: { txt: 'ESTORNO NÃO CONFIRMADO', bg: 'var(--danger,#dc2626)', fg: '#fff' },
    estornada_sem_baixa: { txt: 'Estornado no MP — venda ativa', bg: 'var(--danger,#dc2626)', fg: '#fff' },
  };
  const s = mapa[estado];
  if (!s) return null;
  return (
    <span style={{ backgroundColor: s.bg, color: s.fg, fontWeight: 700, padding: '2px 8px', borderRadius: 999, fontSize: 11, whiteSpace: 'nowrap' }}>
      {s.txt}
    </span>
  );
}

function fmtInstante(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(d);
}

interface ComprovanteResp {
  cobranca: {
    id: number; numero: string | null; venda_numero: string | null; cliente_nome: string;
    valor: string; valor_pago: string | null; card_bandeira: string | null; card_parcelas: number;
    card_nsu: string | null; card_cod_autoriz: string | null; pago_em: string | null; mp_payment_id: string;
  };
  estorno: {
    estado: EstadoCartaoMp; houve: boolean; valor_base: number; valor_ao_pagador: number;
    refund_id: string | null; data: string | null; refund_count: number; mp_status: string | null;
  };
}

// ---- Modal de comprovante de estorno (consulta o MP ao vivo) ----
export function ComprovanteEstornoModal({ cobrancaId, onClose }: { cobrancaId: number; onClose: () => void }) {
  const toast = useToast();
  const [dados, setDados] = useState<ComprovanteResp | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    api.get<ComprovanteResp>(`/api/cartao/cobrancas/${cobrancaId}/estorno`)
      .then((d) => { if (vivo) setDados(d); })
      .catch((e: any) => { if (vivo) setErro(e.message || 'falha ao consultar'); });
    return () => { vivo = false; };
  }, [cobrancaId]);

  function montarTexto(d: ComprovanteResp): string {
    const c = d.cobranca; const e = d.estorno;
    const L: string[] = [];
    L.push('COMPROVANTE DE ESTORNO — Cartão (Mercado Pago)');
    L.push('Fio de Cobre Materiais Elétricos');
    L.push('');
    L.push(`Venda: ${c.venda_numero ?? c.numero ?? '—'}`);
    L.push(`Cliente: ${c.cliente_nome}`);
    L.push(`Cartão: ${c.card_bandeira ?? '—'}${c.card_parcelas > 1 ? ` · ${c.card_parcelas}x` : ''}`);
    if (c.card_cod_autoriz) L.push(`Autorização: ${c.card_cod_autoriz}`);
    L.push(`Valor cobrado: ${formatMoeda(Number(c.valor_pago ?? c.valor))}`);
    L.push(`Pago em: ${fmtInstante(c.pago_em)}`);
    L.push(`Pagamento MP: ${c.mp_payment_id}`);
    L.push('');
    if (e.houve) {
      L.push('>>> ESTORNO CONFIRMADO NO MERCADO PAGO <<<');
      L.push(`Valor devolvido ao cliente: ${formatMoeda(e.valor_ao_pagador)}`);
      L.push(`Nº do estorno (MP): ${e.refund_id ?? '—'}`);
      L.push(`Processado em: ${fmtInstante(e.data)}`);
      L.push('');
      L.push('Observação: o estorno foi processado pela loja no Mercado Pago. O crédito');
      L.push('aparece na fatura do cartão (em geral na fatura seguinte, sobretudo se parcelado).');
    } else {
      L.push('ATENÇÃO: o Mercado Pago NÃO registra estorno para esta cobrança.');
    }
    return L.join('\n');
  }

  async function copiar() {
    if (!dados) return;
    try { await navigator.clipboard.writeText(montarTexto(dados)); toast.sucesso('Comprovante copiado'); }
    catch { toast.erro('Não consegui copiar — selecione e copie manualmente'); }
  }

  function imprimir() {
    if (!dados) return;
    const w = window.open('', '_blank', 'width=420,height=600');
    if (!w) { toast.erro('Permita pop-ups para imprimir'); return; }
    const txt = montarTexto(dados).replace(/&/g, '&amp;').replace(/</g, '&lt;');
    w.document.write(`<pre style="font:13px/1.5 monospace;padding:16px;white-space:pre-wrap">${txt}</pre>`);
    w.document.close(); w.focus(); w.print();
  }

  const e = dados?.estorno;
  const alerta = e && (e.estado === 'estorno_nao_confirmado' || e.estado === 'estornada_sem_baixa');

  return (
    <Modal aberto titulo="Comprovante de estorno" onFechar={onClose} largura={560} compacto>
      <div style={{ padding: 4 }}>
        {erro ? (
          <div style={{ color: 'var(--danger,#dc2626)', padding: 12 }}>{erro}</div>
        ) : !dados ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 16, color: 'var(--text-muted)' }}>
            <Loader2 size={16} className="animate-spin" /> Consultando o Mercado Pago…
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 10 }}><SeloEstorno estado={e!.estado} /></div>
            <div style={{ fontSize: 14, lineHeight: 1.7 }}>
              <div><strong>{dados.cobranca.venda_numero ?? dados.cobranca.numero}</strong> · {dados.cobranca.cliente_nome}</div>
              <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                {dados.cobranca.card_bandeira ?? 'Cartão'}{dados.cobranca.card_parcelas > 1 ? ` · ${dados.cobranca.card_parcelas}x` : ''}
                {dados.cobranca.card_cod_autoriz ? ` · Aut. ${dados.cobranca.card_cod_autoriz}` : ''}
              </div>
              <div>Cobrado: <strong>{formatMoeda(Number(dados.cobranca.valor_pago ?? dados.cobranca.valor))}</strong> · {fmtInstante(dados.cobranca.pago_em)}</div>
            </div>

            {e!.houve ? (
              <div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: alerta ? 'var(--danger,#dc2626)' : '#fde68a', color: alerta ? '#fff' : '#000' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, marginBottom: 4 }}>
                  {alerta ? <AlertTriangle size={16} /> : <CheckCircle2 size={16} />}
                  {e!.estado === 'estornada_sem_baixa' ? 'Estornado no MP — mas a venda segue ativa' : 'Estorno confirmado no Mercado Pago'}
                </div>
                <div style={{ fontSize: 14 }}>Devolvido ao cliente: <strong>{formatMoeda(e!.valor_ao_pagador)}</strong></div>
                <div style={{ fontSize: 13 }}>Nº do estorno: <strong>{e!.refund_id ?? '—'}</strong></div>
                <div style={{ fontSize: 13 }}>Processado em: {fmtInstante(e!.data)}</div>
              </div>
            ) : (
              <div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: e!.estado === 'estorno_nao_confirmado' ? 'var(--danger,#dc2626)' : '#1f2937', color: '#fff' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700 }}>
                  <AlertTriangle size={16} />
                  {e!.estado === 'estorno_nao_confirmado'
                    ? 'O sistema diz estornado, mas o MP NÃO confirmou'
                    : 'Sem estorno registrado no Mercado Pago'}
                </div>
                <div style={{ fontSize: 13, marginTop: 4 }}>
                  Status do pagamento no MP: <strong>{e!.mp_status ?? '—'}</strong>. Confira no painel do Mercado Pago.
                </div>
              </div>
            )}

            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)' }}>
              Estorno aprovado no MP não é a mesma coisa que crédito na fatura: no cartão o crédito cai na fatura do cliente
              (em geral na seguinte, sobretudo parcelado). Este comprovante é a prova pra levar ao banco.
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
              <button type="button" className="btn-secundario" onClick={imprimir} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Printer size={15} /> Imprimir
              </button>
              <button type="button" className="btn-primario" onClick={() => void copiar()} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Copy size={15} /> Copiar comprovante
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

// ---- Modal do auditor de cobertura ----
interface AuditorItem {
  mp_payment_id: string; data: string | null; valor: number; tipo: string | null; bandeira: string | null;
  mp_status: string | null; estornado: number; titular: string | null; ultimos4: string | null;
  external_reference: string | null; no_sistema: boolean; local_id: number | null; local_status: string | null;
  orcamento_id: number | null; pago_sem_baixa: boolean;
  /** 'referencia' = a cobrança existe mas não sabe deste pagamento (a cobrança abandonada). */
  casado_por: 'id' | 'referencia' | null;
}
interface AuditorResp {
  periodo: { inicio: string; fim: string };
  total: number;
  sem_registro_local: number;
  pago_sem_baixa: number;
  // Se a varredura se provou. A busca do MP responde 200 com total 0 de forma intermitente, então
  // "zero órfãos" sem esta prova não quer dizer nada ([DA-718]).
  varredura: {
    valido: boolean; motivo: string | null; janelas: number; esperado: number;
    controles: { exigidos: number; encontrados: number; faltando: string[] };
    janelas_refeitas: number;
  };
  itens: AuditorItem[];
  abandonadas: AuditorAbandonada[];
}
// Cobrança que morreu sem baixa, vinda do BANCO — existe mesmo quando a busca do MP falha.
interface AuditorAbandonada {
  id: number; status: string; external_reference: string | null; valor: string;
  orcamento_id: number | null; criado_em: string; virou_venda: string | null;
}

const hojeISO = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(new Date());

export function AuditorCartaoModal({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const [fim, setFim] = useState(hojeISO());
  const [inicio, setInicio] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(d);
  });
  const [resp, setResp] = useState<AuditorResp | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [soOrfaos, setSoOrfaos] = useState(false);
  const [verCompr, setVerCompr] = useState<number | null>(null);
  const [resgatando, setResgatando] = useState<number | null>(null);
  const dialog = useDialog();

  async function rodar() {
    setCarregando(true);
    try { setResp(await api.get<AuditorResp>('/api/cartao/auditor', { query: { inicio, fim } })); }
    catch (e: any) { toast.erro(e.message); }
    finally { setCarregando(false); }
  }

  // Resgata uma cobrança abandonada pelo caminho oficial (o mesmo do webhook). Ação POR LINHA: não
  // há nada pré-marcado nem lote, porque cada resgate é uma baixa de dinheiro e tem que ser um gesto
  // consciente. A recusa do servidor vem em modal — quem está aqui procura dinheiro sumido, e um
  // toast de 4s não entrega o motivo.
  async function reconsultar(cobrancaId: number | null, valor: number) {
    if (cobrancaId == null) return;
    const ok = await dialog.confirmar(
      `Perguntar ao Mercado Pago o que aconteceu com a cobrança #${cobrancaId} (${formatMoeda(valor)})?\n\n`
      + 'Se o pagamento estiver aprovado lá, a cobrança é dada como PAGA aqui.',
      { titulo: 'Reconsultar no Mercado Pago' },
    );
    if (!ok) return;
    setResgatando(cobrancaId);
    try {
      const r = await api.post<{ mudou: boolean; mensagem: string }>(`/api/cartao/cobranca/${cobrancaId}/reconsultar`, {});
      if (r.mudou) toast.sucesso(r.mensagem); else toast.aviso(r.mensagem);
      await rodar();
    } catch (e: any) {
      void dialog.alerta(e?.message || 'Falha ao reconsultar', { titulo: 'Não deu pra resgatar', tipo: 'erro' });
    } finally { setResgatando(null); }
  }
  useEffect(() => { void rodar(); /* eslint-disable-next-line */ }, []);

  const itens = (resp?.itens ?? []).filter((i) => !soOrfaos || !i.no_sistema);

  return (
    <Modal aberto titulo="Auditor de cartão (Mercado Pago)" onFechar={onClose} largura={900}>
      <div style={{ padding: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>De
            <input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} style={{ width: 150 }} />
          </label>
          <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>até
            <input type="date" value={fim} onChange={(e) => setFim(e.target.value)} style={{ width: 150 }} />
          </label>
          <button type="button" className="btn-primario" onClick={() => void rodar()} disabled={carregando}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {carregando ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />} Auditar
          </button>
          <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={soOrfaos} onChange={(e) => setSoOrfaos(e.target.checked)} />
            Só os sem registro local
          </label>
        </div>

        {/* A VALIDADE vem antes do número. Um resultado que não se provou não pode ser lido como
            "está tudo certo" — era exatamente esse zero que tranquilizava sem ter olhado ([DA-718]).
            Laranja sólido com fonte preta: a cor de atenção da casa, nunca verde/vermelho. */}
        {resp && !resp.varredura.valido && (
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 8, background: '#f97316', color: '#000',
            borderLeft: '5px solid #9a3412', borderRadius: 8, padding: '8px 12px', marginBottom: 10, textTransform: 'none',
          }}>
            <AlertTriangle size={18} style={{ flex: '0 0 auto', marginTop: 1 }} />
            <div style={{ fontSize: 12, fontWeight: 700 }}>
              <div style={{ fontSize: 13, fontWeight: 800 }}>Esta leitura NÃO está completa — não use como conferência.</div>
              <div style={{ marginTop: 2, fontWeight: 600 }}>{resp.varredura.motivo}</div>
              <div style={{ marginTop: 2, fontWeight: 600 }}>A busca do Mercado Pago às vezes responde vazio sem dar erro. Clique em Auditar de novo.</div>
            </div>
          </div>
        )}

        {resp && (
          <div style={{ marginBottom: 10, fontSize: 13 }}>
            {resp.total} pagamento(s) de cartão na maquininha no período · {' '}
            {resp.sem_registro_local > 0
              ? <strong style={{ color: 'var(--danger,#dc2626)' }}>{resp.sem_registro_local} SEM registro no sistema</strong>
              : <span>todos têm registro no sistema ✓</span>}
            {resp.pago_sem_baixa > 0 && (
              <> · <strong style={{ color: 'var(--danger,#dc2626)' }}>{resp.pago_sem_baixa} pago(s) no MP sem baixa aqui</strong></>
            )}
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, textTransform: 'none' }}>
              {resp.varredura.valido ? 'Leitura conferida' : 'Leitura incompleta'} · {resp.varredura.janelas} janela(s) ·{' '}
              {resp.varredura.controles.exigidos > 0
                ? <>{resp.varredura.controles.encontrados} de {resp.varredura.controles.exigidos} pagamentos conhecidos reencontrados</>
                : <>sem pagamento conhecido no período pra conferir a busca</>}
              {resp.varredura.janelas_refeitas > 0 && <> · {resp.varredura.janelas_refeitas} janela(s) refeita(s)</>}
            </div>
          </div>
        )}

        <div style={{ maxHeight: '55vh', overflowY: 'auto' }}>
          <table className="tabela" style={{ width: '100%', fontSize: 12 }}>
            <thead>
              <tr>
                <th>Data</th><th>Pagamento MP</th><th style={{ textAlign: 'right' }}>Valor</th><th>Tipo</th>
                <th style={{ textAlign: 'right' }}>Estornado</th><th>Titular (final)</th><th>Ref.</th><th>No sistema?</th><th />
              </tr>
            </thead>
            <tbody>
              {itens.map((i) => (
                <tr key={i.mp_payment_id} style={!i.no_sistema || i.pago_sem_baixa ? { backgroundColor: 'var(--devolucao-bg,#fff7ed)' } : undefined}>
                  <td>{fmtInstante(i.data)}</td>
                  <td>{i.mp_payment_id}</td>
                  <td className="cel-num" style={{ textAlign: 'right', fontWeight: 700 }}>{formatMoeda(i.valor)}</td>
                  <td>{i.tipo === 'debit_card' ? 'Débito' : 'Crédito'}{i.bandeira ? ` · ${i.bandeira}` : ''}</td>
                  <td className="cel-num" style={{ textAlign: 'right', color: i.estornado > 0.005 ? 'var(--danger,#dc2626)' : 'var(--text-muted)' }}>
                    {i.estornado > 0.005 ? formatMoeda(i.estornado) : '—'}
                  </td>
                  <td>{i.titular ?? '—'}{i.ultimos4 ? ` ····${i.ultimos4}` : ''}</td>
                  <td>{i.external_reference ?? '—'}</td>
                  <td>
                    {!i.no_sistema
                      ? <span style={{ backgroundColor: 'var(--danger,#dc2626)', color: '#fff', fontWeight: 700, padding: '2px 8px', borderRadius: 999 }}>fora do sistema</span>
                      : i.pago_sem_baixa
                        // Aprovado no MP e a cobrança aqui não está paga: é dinheiro que entrou e
                        // não virou venda, com registro local. Antes saía cinza, como resolvido.
                        ? <span style={{ backgroundColor: '#f97316', color: '#000', fontWeight: 800, padding: '2px 8px', borderRadius: 999 }}>
                            pago no MP · aqui {i.local_status}
                            {i.casado_por === 'referencia' ? ' · cobrança não sabe do pagamento' : ''}
                          </span>
                        : <span style={{ color: 'var(--text-muted)' }}>{i.local_status ?? 'sim'}{i.orcamento_id ? ` · ORC ${i.orcamento_id}` : ''}</span>}
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {/* Gesto POR LINHA, e só onde ele faz sentido: cobrança local que o MP diz
                        estar paga. Nada pré-marcado, nada em lote — cada resgate é uma baixa. */}
                    {i.pago_sem_baixa && i.local_id != null && (
                      <button
                        type="button" onClick={() => void reconsultar(i.local_id, i.valor)} disabled={resgatando != null}
                        style={{
                          fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4,
                          background: '#1d4ed8', color: '#fff', border: 0, borderRadius: 5, padding: '4px 9px',
                          marginRight: 4, cursor: resgatando != null ? 'not-allowed' : 'pointer',
                          opacity: resgatando != null ? 0.6 : 1,
                        }}
                        title="Perguntar ao Mercado Pago e dar baixa se estiver pago"
                      >
                        {resgatando === i.local_id ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                        Reconsultar
                      </button>
                    )}
                    {i.local_id != null && (
                      <button type="button" className="btn-secundario" style={{ fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                        onClick={() => setVerCompr(i.local_id)} title="Comprovante de estorno desta cobrança">
                        <FileText size={12} />Comprovante
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {!carregando && itens.length === 0 && (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)' }}>Nada no período/filtro.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Cobranças que morreram sem baixa, vindas do BANCO. Ficam aqui porque o resgate NÃO pode
            depender da busca do MP: se ela falhar, o pagamento não aparece na tabela acima, a linha
            some — e o botão sumiria justo na hora em que ele é necessário. Esta lista não tem
            intermitência. */}
        {resp && resp.abandonadas.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>
              Cobranças que morreram sem baixa ({resp.abandonadas.length})
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6, textTransform: 'none' }}>
              Foram canceladas, recusadas ou expiraram. Se desconfiar que alguma foi paga mesmo assim, pergunte ao Mercado Pago aqui —
              não depende da busca acima. As que já viraram venda foram resolvidas por outro caminho.
            </div>
            <div style={{ maxHeight: '26vh', overflowY: 'auto' }}>
              <table className="tabela" style={{ width: '100%', fontSize: 12 }}>
                <thead>
                  <tr>
                    <th>Data</th><th>Cobrança</th><th style={{ textAlign: 'right' }}>Valor</th>
                    <th>Situação</th><th>Referência</th><th>Virou venda?</th><th />
                  </tr>
                </thead>
                <tbody>
                  {resp.abandonadas.map((a) => (
                    <tr key={a.id}>
                      <td>{fmtInstante(a.criado_em)}</td>
                      <td>#{a.id}{a.orcamento_id ? ` · ORC ${a.orcamento_id}` : ''}</td>
                      <td className="cel-num" style={{ textAlign: 'right', fontWeight: 700 }}>{formatMoeda(Number(a.valor))}</td>
                      <td>{a.status}</td>
                      <td>{a.external_reference ?? '—'}</td>
                      <td>{a.virou_venda
                        ? <span style={{ color: 'var(--text-muted)' }}>{a.virou_venda}</span>
                        : <strong>não</strong>}</td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          type="button" onClick={() => void reconsultar(a.id, Number(a.valor))} disabled={resgatando != null}
                          style={{
                            fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4,
                            background: '#1d4ed8', color: '#fff', border: 0, borderRadius: 5, padding: '4px 9px',
                            cursor: resgatando != null ? 'not-allowed' : 'pointer', opacity: resgatando != null ? 0.6 : 1,
                          }}
                          title="Perguntar ao Mercado Pago o que aconteceu com esta cobrança"
                        >
                          {resgatando === a.id ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                          Reconsultar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <RefreshCw size={12} /> A busca padrão do MP não lista cartão — o auditor usa o filtro de maquininha (pos_payment) pra pegar tudo, inclusive testes feitos fora do sistema.
        </div>
      </div>
      {verCompr != null && <ComprovanteEstornoModal cobrancaId={verCompr} onClose={() => setVerCompr(null)} />}
    </Modal>
  );
}
